import {cancel, invoke, on} from "@hybird/core/bridge/hybirdBridge";
import {BridgeMethod} from "@hybird/core/bridge/core";
import {defaultError, WebError} from "@hybird/core/error";
import {flutterCore, permissions} from "@hybird/flutter/bridge/core";

/**
 * YdaCamera类
 */
export class FlutterCamera extends BridgeMethod {
  constructor() {
    super();
  }

  cameraType;

  /** 相机初始化，此方法会拉起相机视图
   *
   * @param {object} argInfo 此参数为必填
   * 对象中必须包含
   * [previewSize] 预览尺寸
   * [captureSize] 拍照尺寸
   * [analyzeSize] 分析尺寸
   * [recordSize] 录像尺寸
   * [debug]
   * [cameraType]
   * [deviceType]
   * [quarterTurns] 旋转角度
   * [mirror] 镜像方向  x or y
   * [zoomPercent] 缩放百分比 (0-100)
   * @return {Promise<boolean>} boolean
   */
  async startCamera(argInfo) {
    if (!this.cameraType) {
      this.cameraType = argInfo.cameraType;
    }

    const flag = await flutterCore.requestPermission([permissions.camera]);

    if (!flag.isGranted) {
      throw WebError.code(defaultError.NOT_PERMISSION, {
        msg: "摄像头权限未授权, 请前往设置授权",
        data: flag.denied,
      });
    }

    return invoke("init", {
      params: argInfo,
    });
  }

  /**
   * 相机结果回调方法，此方法回调拍照，识别，录像的结果
   * @param event
   * @param filter
   */
  onListenStreamData(event, {filter} = {}) {
    /// * 相机启动结果 photoPath: 照片路径, recordPath：录制路径, textureId: 相机映射id，storageSize: 剩余储存大小/总大小
    /// * { type: 1, data: { photoPath: '', recordPath: '', textureId: 1 }, takeTime: 120 }
    /// * 拍照结果返回 photoId: 接收到的id, path: 图片路径, takeTime: 时长
    /// * { type: 2, data: { photoId: 123, path: '' }, takeTime: 120 }
    /// * 分析结果返回 list: 结果
    /// * { type: 3, data: { list: [] } }
    /// * 录像结果返回 path: 录像结果路径返回, recordId: 传过来的id, takeTime: 时长
    /// * { type: 4, data: { recordId: 121, path: '' }, takeTime: 123 }
    /// * 当前设备磁盘情况 storageSize 磁盘情况, canWork: 是否可以继续正常工作:3s/1.5GB
    /// * { type: 5, data: { storageSize: 120MB/3000MB, canWork: false } }
    /// * 返回扫码结果
    /// * { type: 6, data: { code: '' } }
    return on("onListenStreamData", event, {filter});
  }

  /**
   * 相机结果回调方法，此方法回调拍照，识别，录像的结果
   * @returns {null} 返回的result对象，包含拍照，识别，录像的结果
   * 例：{}
   * @param event
   */
  offListenStreamData(event) {
    return cancel("onListenStreamData", {event});
  }

  /**
   * 相机拍照方法
   *
   * @param {number} photoId 此参数为必填
   * @return {Promise<String>} boolean
   */
  async takePhoto(photoId = 0, {cameraType = this.cameraType} = {}) {
    return new Promise((resolve, reject) => {
      const onListenStreamData = async (data) => {
        if (data.type === 2 && data.data.photoId === photoId) {
          resolve(data.data.path);
          this.offListenStreamData(onListenStreamData);
        }
      };

      this.onListenStreamData(onListenStreamData).then(() => {
        return invoke("takePhoto", {
          params: {photoId: photoId, cameraType},
        });
      });
    });
  }

  /**
   * 清空文件
   * @param list{Array} 0:视频，1:照片
   */
  async deleteMediaFiles(list = []) {
    return invoke("deleteMediaFiles", {params: {list}});
  }

  /**
   * 清空文件
   * @param list{string[]}
   */
  async deleteFilesByPaths(list = []) {
    const path = [];
    for (const i in list) {
      let file = list[i];
      if (typeof file === "string") {
        if (file.startsWith("file://")) {
          file = file.replaceAll("file://", "");
        }

        const end = file.indexOf("?filename");

        if (end > -1) {
          file = file.substring(0, end);
        }

        path.push(file);
      }
    }
    console.log('要删除的文件', JSON.stringify(path))

    return invoke("deleteFilesByPaths", {params: {list: path}});
  }

  /**
   * 开始分析
   * @param {null} 无参数
   * @return {Promise<boolean>} boolean
   */
  async startAnalyze({cameraType = this.cameraType} = {}) {
    return invoke("startAnalyze", {
      params: {
        cameraType
      }
    });
  }

  /**
   * 开始空白页识别
   * @param {String} cameraType 相机类型
   * @param {boolean} enablePerceptualHash 是否启用感知哈希比对
   * @param {Array<String>} hashList 特征值列表
   * @param {number} similarityThreshold 相似度阈值 (1-100)
   * @return {Promise<boolean>} boolean
   */
  async startBlankDetect({
    cameraType = this.cameraType,
    enablePerceptualHash,
    hashList,
    similarityThreshold
  } = {}) {
    console.log('startBlankDetect camera',enablePerceptualHash,hashList,similarityThreshold)
    return invoke("startBlankDetect", {
      params: {
        cameraType,
        enablePerceptualHash,
        hashList,
        similarityThreshold
      }
    });
  }

  /**
   * 停止空白页识别
   * @param {null} 无参数
   * @return {Promise<boolean>} boolean
   */
  async stopBlankDetect({cameraType = this.cameraType} = {}) {
    return invoke("stopBlankDetect", {
      params: {
        cameraType
      }
    });
  }

  /**
   * 开始遮挡检测
   * @param {null} 无参数
   * @return {boolean} boolean*
   * @return {Promise<boolean>} boolean
   */
  async startOcclusionDetect({cameraType = this.cameraType} = {}) {
    return invoke("startOcclusionDetect", {
      params: {
        cameraType
      }
    });
  }

  /**
   * 停止遮挡检测
   * @param {null} 无参数
   * @return {boolean} boolean*
   * @return {Promise<boolean>} boolean
   */
  async stopOcclusionDetect({cameraType = this.cameraType} = {}) {
    return invoke("stopOcclusionDetect", {
      params: {
        cameraType
      }
    });
  }

  /**
   * 开始分析
   * @return {Promise<boolean>} boolean
   */
  async startRecord(recordId = 0, splitMinutes = 15, {cameraType = this.cameraType} = {}) {
    return invoke("startRecord", {
      params: {recordId: recordId, splitMinutes: splitMinutes, cameraType},
    });
  }

  /**
   * 停止分析
   * @return {Promise<boolean>} boolean
   */
  async stopAnalyze({cameraType = this.cameraType} = {}) {
    return invoke("stopAnalyze", {
      params: {
        cameraType
      }
    });
  }

  /**
   * 停止录像
   * @return {Promise<boolean>} boolean
   */
  async stopRecord({cameraType = this.cameraType} = {}) {
    return invoke("stopRecord", {
      params: {
        cameraType
      }
    });
  }

  /**
   * 开始code
   * @return {Promise<boolean>} boolean
   */
  async startScanCode({cameraType = this.cameraType} = {}) {
    return invoke("startScanCode", {
      params: {
        cameraType
      }
    });
  }

  /**
   * 停止code
   * @return {Promise<boolean>} boolean
   */
  async stopScanCode({cameraType = this.cameraType} = {}) {
    return invoke("stopScanCode", {
      params: {
        cameraType
      }
    });
  }

  /**
   * 开始清晰度检测
   * @return {Promise<boolean>} boolean
   */
  async startClarity({cameraType = this.cameraType} = {}) {
    return invoke("startClarity", {
      params: {
        cameraType
      }
    });
  }

  /**
   * 停止清晰度检测
   * @return {Promise<boolean>} boolean
   */
  async stopClarity({cameraType = this.cameraType} = {}) {
    return invoke("stopClarity", {
      params: {
        cameraType
      }
    });
  }
  
  /**
   * 开始裸章用印检测
   * @return {Promise<boolean>} boolean
   */
  async startSealAnalyze() {
    console.log('参数氮气', 'flutter.startSealAnalyze');

    return invoke("startSealAnalyze");
  }

  /**
   * 停止裸章用印检测
   * @return {Promise<boolean>} boolean
   */
  async stopSealAnalyze() {
    return invoke("stopSealAnalyze");
  }


  /**
   * 资源释放
   * @return {Promise<boolean>} boolean
   */
  async release({cameraType = this.cameraType} = {}) {
    return invoke("release", {
      params: {
        cameraType
      }
    }).then(data => {
      if (this.cameraType === cameraType) {
        this.cameraType = null;
      }

      return data;
    });
  }
}

export const flutterCamera = new FlutterCamera();
